/* Chip CSS Module */

/* Base chip reset and layout */
.chip {
  align-items: center;
  background: none;
  border: none;
  border-radius: 100px;
  box-shadow: none;
  display: inline-flex;
  max-width: 100%;
  outline: none;
  overflow: hidden;
  transition: background-color 50ms linear, color 50ms linear;
  user-select: none;
  vertical-align: top;

  /* Default spacing */
  --vapor-chip-gap: var(--spaces-4);
  --vapor-chip-pl: var(--spaces-8);
  --vapor-chip-pr: var(--spaces-8);

  gap: var(--vapor-chip-gap);
  height: var(--vapor-chip-height);
  padding-left: var(--vapor-chip-pl);
  padding-right: var(--vapor-chip-pr);

  /* Default text color from meta color */
  color: var(--vapor-chip-text);
}

/** Spacing: avatar */
.chip[data-has-avatar][data-size='medium'] {
  --vapor-chip-pl: var(--spaces-2);
}

.chip[data-has-avatar][data-size='small'] {
  --vapor-chip-pl: var(--spaces-1);
}

/** Spacing: icon before (only if no avatar) */
.chip[data-has-icon-before]:not([data-has-avatar]) {
  --vapor-chip-gap: var(--spaces-2);
  --vapor-chip-pl: var(--spaces-4);
}

/** Spacing: icon after */
.chip[data-has-icon-after] {
  --vapor-chip-gap: var(--spaces-2);
  --vapor-chip-pr: var(--spaces-4);
}

/** Spacing: delete button (only if no custom icon after) */
.chip[data-has-delete]:not([data-has-icon-after]) {
  --vapor-chip-pr: var(--spaces-4);
}

/* Delete button with no padding when not selectable (close button is separate) */
.chip[data-has-delete]:not([data-has-icon-after]):not([data-selectable]) {
  --vapor-chip-pr: 0;
}

/** Sizes */
.chip[data-size='medium'] {
  --vapor-chip-height: var(--spaces-24);
}

.chip[data-size='small'] {
  --vapor-chip-height: 20px;
}

/** Variant: fill (default) */
.chip[data-variant='fill'] {
  background-color: var(--vapor-chip-color-low);
}

/* Fill variant: highlighted state when Text has a match */
.chip[data-variant='fill']:has(mark) {
  background-color: var(--vapor-chip-color-hover);
}

/* Fill variant: hover/active state for button chips */
button.chip[data-variant='fill']:where(:hover, :active, .hover, .active) {
  background-color: var(--vapor-chip-color-hover);
}

/* Fill variant: focused state for selectable - "selected" appearance */
.chip[data-variant='fill'][data-selectable]:where(:focus, .focus) {
  background-color: var(--vapor-chip-color-active);
  color: var(--colors-components-chip-text);
}

/* Fill variant: focused + hovered - stronger "selected" appearance */
.chip[data-variant='fill'][data-selectable]:where(:focus, .focus):where(
    :hover,
    :active,
    .hover,
    .active
  ) {
  background-color: var(--vapor-chip-color-opaque);
}

/** Variant: ghost */
.chip[data-variant='ghost'] {
  background-color: transparent;
  border: 1px solid var(--vapor-chip-color-medium);
}

/* Ghost variant: highlighted state when Text has a match */
.chip[data-variant='ghost']:has(mark) {
  background-color: var(--vapor-chip-color-low);
  border-color: var(--vapor-chip-color-medium-hover);
}

/* Ghost variant: hover/active state for button chips */
button.chip[data-variant='ghost']:where(:hover, :active, .hover, .active) {
  background-color: var(--vapor-chip-color-low);
  border-color: var(--vapor-chip-color-medium-hover);
}

/* Ghost variant: focused state - "selected" appearance */
.chip[data-variant='ghost'][data-selectable]:where(:focus, .focus) {
  background-color: var(--vapor-chip-color-active);
  border-color: transparent;
  color: var(--colors-components-chip-text);
}

/* Ghost variant: focused + hovered - stronger "selected" appearance */
.chip[data-variant='ghost'][data-selectable]:where(:focus, .focus):where(
    :hover,
    :active,
    .hover,
    .active
  ) {
  background-color: var(--vapor-chip-color-opaque);
}

/** Variant: stark */
.chip[data-variant='stark'] {
  background-color: var(--colors-components-player-background-control);
  color: var(--colors-components-player-text);
}

/* Stark variant: hover/active state for button chips */
button.chip[data-variant='stark']:where(:hover, :active, .hover, .active) {
  opacity: 0.85;
}

/** Button chips: pointer cursor */
button.chip {
  cursor: pointer;
}

/** Focus visible ring */
.chip:where(:focus-visible, .focus) {
  outline: 1px solid var(--colors-border-interactive-focused);
  outline-offset: 1px;
}

/** Disabled state */
.chip:disabled,
.chip[disabled] {
  background-color: var(--colors-background-interactive-disabled);
  opacity: 0.7;
  pointer-events: none;
}
